/**
 * Scan feedback tones, synthesised — no assets, no network.
 *
 * The sound of the moment a card is logged tells the user what happened without
 * looking away from the table: a short tick for bulk (under $2.50), one ding for
 * $2.50-5, and a rising triple for $5+. Prices are integer cents off the catalogue; a
 * printing with no price at all ticks like bulk, so every logged scan is audible.
 *
 * iOS only lets an AudioContext start inside a user gesture, so {@link armChime} is
 * called from the Start button's click handler; everything after that is free to play
 * from the scan loop.
 */

let context: AudioContext | null = null;

/** Create or resume the audio context. Must be called from a user gesture once. */
export function armChime(): void {
  try {
    context ??= new AudioContext();
    if (context.state === "suspended") void context.resume();
  } catch {
    // No audio support is a shrug, not an error — scanning works silently.
    context = null;
  }
}

/** One decaying strike: a fundamental plus a bell-ish partial. */
function strike(at: number, frequency: number, duration: number, peak: number): void {
  if (!context) return;
  for (const [ratio, share] of [
    [1, 1],
    [2.76, 0.35],
  ] as const) {
    const osc = context.createOscillator();
    const gain = context.createGain();
    osc.type = "sine";
    osc.frequency.value = frequency * ratio;
    gain.gain.setValueAtTime(0, at);
    gain.gain.linearRampToValueAtTime(peak * share, at + 0.008);
    gain.gain.exponentialRampToValueAtTime(1e-4, at + duration);
    osc.connect(gain).connect(context.destination);
    osc.start(at);
    osc.stop(at + duration + 0.05);
  }
}

/** Play the tone tier for a price in integer cents (null = unpriced, treated as bulk). */
export function scanChime(cents: number | null): void {
  if (!context || context.state !== "running") return;
  const now = context.currentTime;
  if (cents == null || cents < 250) {
    // Bulk: a dry, quiet tick — present but not worth a glance.
    strike(now, 2200, 0.07, 0.1);
    return;
  }
  if (cents < 500) {
    strike(now, 880, 0.4, 0.22);
    return;
  }
  // $5+: a rising major arpeggio — the one worth looking up for.
  strike(now, 880, 0.35, 0.22);
  strike(now + 0.17, 1046.5, 0.35, 0.22);
  strike(now + 0.34, 1318.5, 0.45, 0.24);
}
